import { useState } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Paper,
    Stack,
    Avatar,
    Typography,
    IconButton,
    Chip,
    Menu,
    MenuItem,
    Tooltip,
    Slider
} from '@mui/material';
import { styled, useTheme, alpha } from '@mui/material/styles';
import Iconify from 'src/components/iconify';

// Version 1.1 - Added Node-to-Node Swapping and Fixed ESLint rules

// Styled components for Org Chart lines
const StyledNodeContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    position: 'relative',
    paddingTop: 20,
}));

// Vertical line above node (connects to parent)
const StyledNodeLineTop = styled(Box)(({ theme }) => ({
    position: 'absolute',
    top: 0,
    left: '50%',
    width: 2,
    height: 20,
    backgroundColor: theme.palette.divider,
    transform: 'translateX(-50%)',
}));

// Vertical line below node (connects to children)
const StyledNodeLineBottom = styled(Box)(({ theme }) => ({
    width: 2,
    height: 20,
    backgroundColor: theme.palette.divider,
}));

// Container for children row
const StyledChildrenContainer = styled(Stack)(({ theme }) => ({
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'center',
    paddingTop: 0,
    position: 'relative',
}));

// Horizontal line connecting children
const StyledConnectorLine = styled(Box)(({ theme, first, last }) => ({
    position: 'absolute',
    top: 0,
    left: first ? '50%' : 0,
    right: last ? '50%' : 0,
    height: 2,
    backgroundColor: theme.palette.divider,
}));

/**
 * Recursive Org Chart Node Component
 */
function OrgChartNode({
    node,
    onRemoveNode,
    onRemoveBranch,
    onAddChild,
    onDropUser,
    onSwapUser,
    onSwapRequest,
    onSwapNodes,
    onDragToggle,
    level = 0,
    isDragging,
    zoom = 1,
    isMigrationContext = false,
    migrationInfo = null,
    project = null
}) {
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = useState(null);
    const [dropMenuAnchor, setDropMenuAnchor] = useState(null);
    const [draggedUser, setDraggedUser] = useState(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [isDropZoneOver, setIsDropZoneOver] = useState(false);

    const handleMenuOpen = (event) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleRemoveNode = () => {
        onRemoveNode(node.id);
        handleMenuClose();
    };

    const handleRemoveBranch = () => {
        onRemoveBranch(node.id);
        handleMenuClose();
    };

    const handleAddChild = () => {
        onAddChild(node.id);
        handleMenuClose();
    };

    const handleSwapRequest = () => {
        onSwapRequest(node.id);
        handleMenuClose();
    };

    const handleDragStartNode = (e) => {
        // Required for some browsers to initiate drag
        e.dataTransfer.setData('text/plain', node.id);
        
        e.dataTransfer.setData('sourceNodeId', node.id);
        e.dataTransfer.setData('userId', node.userId);
        e.dataTransfer.setData('userName', node.name);
        
        e.dataTransfer.effectAllowed = 'copy';
        onDragToggle(true);
    };

    const handleDragEndNode = (e) => {
        e.stopPropagation();
        onDragToggle(false);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
        e.dataTransfer.dropEffect = 'copy';
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);

        const userId = e.dataTransfer.getData('userId');
        const userName = e.dataTransfer.getData('userName');
        const sourceNodeId = e.dataTransfer.getData('sourceNodeId');

        // Prevent dropping a node onto itself
        if (sourceNodeId === node.id) return;

        if (userId && userName) {
            setDraggedUser({ userId, userName, sourceNodeId });
            setDropMenuAnchor(e.currentTarget);
        }
    };

    const handleDropMenuClose = () => {
        setDropMenuAnchor(null);
        setDraggedUser(null);
    };

    const handleConfirmSwap = () => {
        if (draggedUser) {
            if (draggedUser.sourceNodeId) {
                // Node-to-node swap
                onSwapNodes(draggedUser.sourceNodeId, node.id);
            } else {
                // User-list-to-node swap
                onSwapUser(node.id, draggedUser.userId, draggedUser.userName);
            }
        }
        handleDropMenuClose();
    };

    const handleConfirmAddSubordinate = () => {
        if (draggedUser) {
            onDropUser(node.id, draggedUser.userId, draggedUser.userName, draggedUser.sourceNodeId);
        }
        handleDropMenuClose();
    };

    // Drag handlers for Drop Zone
    const handleDropZoneDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDropZoneOver(true);
        e.dataTransfer.dropEffect = 'copy';
    };

    const handleDropZoneDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDropZoneOver(false);
    };

    const handleDropZoneDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDropZoneOver(false);

        const userId = e.dataTransfer.getData('userId');
        const userName = e.dataTransfer.getData('userName');
        const sourceNodeId = e.dataTransfer.getData('sourceNodeId');

        if (userId && userName) {
            onDropUser(node.id, userId, userName, sourceNodeId);
        }
    };

    const hasChildren = node.children && node.children.length > 0;

    const nodeUserId = node.userId?._id || node.userId;
    const isTarget = isMigrationContext && migrationInfo && nodeUserId && nodeUserId.toString() === migrationInfo.targetUserId?.toString();

    const checkIsLocationCompatible = () => {
        if (!migrationInfo || !project) return true;
        const { toStates = [], toCities = [] } = migrationInfo;
        
        const projectStateIds = (project.states || []).map(s => (s._id || s.id || s).toString());
        const projectCityIds = (project.cities || []).map(c => (c._id || c.id || c).toString());

        if (projectStateIds.length === 0 && projectCityIds.length === 0) return true;

        const stateOverlap = toStates.some(sId => projectStateIds.includes(sId.toString()));
        const cityOverlap = toCities.some(cId => projectCityIds.includes(cId.toString()));

        return stateOverlap || cityOverlap;
    };

    const isCompatible = checkIsLocationCompatible();

    // Node styling
    const getNodeBgColor = () => {
        if (isDragOver) return 'success.lighter';
        if (isTarget) {
            if (migrationInfo.migrationType === 'resignation_exit') return alpha(theme.palette.error.main, 0.08);
            return isCompatible ? alpha(theme.palette.success.main, 0.08) : alpha(theme.palette.warning.main, 0.08);
        }
        if (node.role === 'leader') return 'primary.lighter';
        return 'background.paper';
    };

    const getNodeBorderColor = () => {
        if (isDragOver) return 'success.main';
        if (isTarget) {
            if (migrationInfo.migrationType === 'resignation_exit') return 'error.main';
            return isCompatible ? 'success.main' : 'warning.main';
        }
        if (node.role === 'leader') return 'primary.main';
        return 'divider';
    };

    const getNodeBorderWidth = () => {
        if (isDragOver || isTarget) return 2;
        if (node.role === 'leader') return 2;
        return 1;
    };

    const getMigrationLabel = () => {
        if (!isTarget || !migrationInfo) return null;
        const { migrationType } = migrationInfo;
        if (migrationType === 'resignation_exit') return 'Leaving';
        if (migrationType === 'transfer') {
            return isCompatible ? 'Transferring' : 'Transferring - Out of Location';
        }
        if (migrationType === 'promotion') {
            return isCompatible ? 'Promoting' : 'Promoting - Out of Location';
        }
        return 'Migrating';
    };

    const migrationLabel = getMigrationLabel();

    const getMigrationColor = () => {
        if (!migrationInfo) return 'default';
        if (migrationInfo.migrationType === 'resignation_exit') return 'error';
        return isCompatible ? 'success' : 'warning';
    };

    const migrationColor = getMigrationColor();

    // Dimensions driven by zoom
    const cardW = Math.max(120, Math.round(200 * zoom));
    const avatarSz = Math.max(24, Math.round(48 * zoom));
    const nodePad = Math.max(8, Math.round(16 * zoom));

    return (
        <StyledNodeContainer>
            {/* Top Line (only if not root) */}
            {level > 0 && <StyledNodeLineTop />}

            {/* The Node Card */}
            <Box
                draggable
                onDragStart={handleDragStartNode}
                onDragEnd={handleDragEndNode}
                sx={{ cursor: 'grab', '&:active': { cursor: 'grabbing' } }}
            >
                <Paper
                    elevation={isDragOver ? 8 : 2}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    sx={{
                    p: `${nodePad}px`,
                    width: cardW,
                    minWidth: cardW,
                    maxWidth: cardW,
                    borderRadius: 2,
                    bgcolor: getNodeBgColor(),
                    borderColor: getNodeBorderColor(),
                    borderWidth: getNodeBorderWidth(),
                    borderStyle: isDragOver ? 'dashed' : 'solid',
                    transition: 'width 0.2s ease, padding 0.2s ease',
                    position: 'relative',
                    zIndex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    gap: 1,
                    overflow: 'hidden',
                }}
            >
                <Stack direction="row" alignItems="center" justifyContent="flex-end" sx={{ width: '100%', position: 'absolute', top: 4, right: 4 }}>
                    <IconButton size="small" onClick={handleMenuOpen}>
                        <Iconify icon="solar:menu-dots-bold" width={16} />
                    </IconButton>
                </Stack>

                <Avatar
                    sx={{
                        bgcolor: node.role === 'leader' ? 'primary.main' : 'info.main',
                        width: avatarSz,
                        height: avatarSz,
                        fontSize: `${Math.max(0.6, avatarSz / 36)}rem`,
                        mb: 0.5,
                        transition: 'width 0.2s ease, height 0.2s ease',
                    }}
                >
                    {node.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U'}
                </Avatar>

                <Box sx={{ width: '100%' }}>
                    <Typography
                        variant="subtitle2"
                        sx={{
                            lineHeight: 1.2,
                            fontSize: '0.8rem',
                            wordBreak: 'break-word',
                            whiteSpace: 'normal',
                        }}
                    >
                        {node.name || 'Unknown User'}
                    </Typography>

                    {node.role === 'leader' && (
                        <Chip
                            label="Project Leader"
                            color="primary"
                            size="small"
                            sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }}
                        />
                    )}

                    {migrationLabel && (
                        <Chip
                            label={migrationLabel}
                            color={migrationColor}
                            size="small"
                            sx={{ mt: 0.5, height: 20, fontSize: '0.7rem', fontWeight: 'bold' }}
                        />
                    )}
                </Box>

                {isDragOver && (
                    <Chip
                        label="Drop here"
                        color="success"
                        size="small"
                        icon={<Iconify icon="solar:download-minimalistic-bold" width={14} />}
                        sx={{ mt: 1 }}
                    />
                )}

                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <MenuItem onClick={handleAddChild} sx={{ fontSize: '0.875rem' }}>
                        <Iconify icon="solar:add-circle-bold" sx={{ mr: 1 }} width={18} />
                        Add Member
                    </MenuItem>
                    <MenuItem onClick={handleSwapRequest} sx={{ fontSize: '0.875rem' }}>
                        <Iconify icon="solar:transfer-horizontal-bold" sx={{ mr: 1, color: 'info.main' }} width={18} />
                        Swap User
                    </MenuItem>
                    {node.role !== 'leader' && (
                        <>
                            <MenuItem onClick={handleRemoveNode} sx={{ color: 'warning.main', fontSize: '0.875rem' }}>
                                <Iconify icon="solar:user-cross-bold" sx={{ mr: 1 }} width={18} />
                                Remove Node (Promote children)
                            </MenuItem>
                            <MenuItem
                                onClick={handleRemoveBranch}
                                disabled={!hasChildren}
                                sx={{ color: 'error.main', fontSize: '0.875rem' }}
                            >
                                <Iconify icon="solar:trash-bin-trash-bold" sx={{ mr: 1 }} width={18} />
                                Remove Branch
                            </MenuItem>
                        </>
                    )}
                </Menu>

                {/* Drop Action Menu */}
                <Menu
                    anchorEl={dropMenuAnchor}
                    open={Boolean(dropMenuAnchor)}
                    onClose={handleDropMenuClose}
                    anchorOrigin={{ vertical: 'center', horizontal: 'center' }}
                    transformOrigin={{ vertical: 'center', horizontal: 'center' }}
                    disableRestoreFocus
                >
                    <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.neutral' }}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                            {draggedUser?.sourceNodeId ? `Swap with ${draggedUser.userName}` : `Action for ${draggedUser?.userName}`}
                        </Typography>
                    </Box>
                    <MenuItem onClick={handleConfirmSwap}>
                        <Iconify icon="solar:transfer-horizontal-bold" sx={{ mr: 1, color: 'info.main' }} width={18} />
                        Swap User
                    </MenuItem>
                    {!draggedUser?.sourceNodeId && (
                        <MenuItem onClick={handleConfirmAddSubordinate}>
                            <Iconify icon="solar:add-circle-bold" sx={{ mr: 1, color: 'success.main' }} width={18} />
                            Add Subordinate
                        </MenuItem>
                    )}
                </Menu>
            </Paper>
        </Box>

            {/* Add Action Area - Drop Zone or Plus Button */}
            <Box sx={{ position: 'relative', py: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 10 }}>
                {isDragging ? (
                    <Box
                        onDragOver={handleDropZoneDragOver}
                        onDragLeave={handleDropZoneDragLeave}
                        onDrop={handleDropZoneDrop}
                        sx={{
                            width: 160,
                            height: 40,
                            border: 2,
                            borderStyle: 'dashed',
                            borderColor: isDropZoneOver ? 'success.main' : 'divider',
                            borderRadius: 1,
                            bgcolor: isDropZoneOver ? 'success.lighter' : 'background.paper',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            opacity: 0.8,
                            boxShadow: 1,
                            '&:hover': {
                                borderColor: 'success.main',
                                bgcolor: 'success.lighter'
                            }
                        }}
                    >
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Iconify
                                icon="solar:add-circle-bold"
                                width={20}
                                sx={{ color: isDropZoneOver ? 'success.main' : 'text.disabled' }}
                            />
                            <Typography
                                variant="caption"
                                color={isDropZoneOver ? 'success.main' : 'text.disabled'}
                                fontWeight="bold"
                            >
                                Add Subordinate
                            </Typography>
                        </Stack>
                    </Box>
                ) : (
                    <Stack alignItems="center" spacing={0}>
                        <Box sx={{ width: 2, height: 12, bgcolor: 'divider' }} />

                        <IconButton
                            onClick={handleAddChild}
                            size="small"
                            sx={{
                                color: 'primary.main',
                                bgcolor: 'background.paper',
                                border: 1,
                                borderColor: 'divider',
                                width: 28,
                                height: 28,
                                boxShadow: 1,
                                '&:hover': {
                                    bgcolor: 'primary.lighter',
                                    borderColor: 'primary.main',
                                },
                            }}
                        >
                            <Iconify icon="solar:add-circle-bold" width={18} />
                        </IconButton>

                        {hasChildren && <Box sx={{ width: 2, height: 12, bgcolor: 'divider' }} />}
                    </Stack>
                )}
            </Box>

            {/* Creating the branch visual structure */}
            {hasChildren && (
                <>
                    {/* Children Container */}
                    <StyledChildrenContainer spacing={Math.max(1, Math.round(4 * zoom))}>
                        {node.children.map((child, index) => (
                            <Box key={child.id} sx={{ position: 'relative' }}>
                                {/* Horizontal Line Connector logic */}
                                <StyledConnectorLine
                                    first={index === 0}
                                    last={index === node.children.length - 1}
                                />

                                <OrgChartNode
                                    node={child}
                                    onRemoveNode={onRemoveNode}
                                    onRemoveBranch={onRemoveBranch}
                                    onAddChild={onAddChild}
                                    onDropUser={onDropUser}
                                    onSwapUser={onSwapUser}
                                    onSwapRequest={onSwapRequest}
                                    onSwapNodes={onSwapNodes}
                                    onDragToggle={onDragToggle}
                                    level={level + 1}
                                    isDragging={isDragging}
                                    zoom={zoom}
                                    isMigrationContext={isMigrationContext}
                                    migrationInfo={migrationInfo}
                                    project={project}
                                />
                            </Box>
                        ))}
                    </StyledChildrenContainer>
                </>
            )}
        </StyledNodeContainer>
    );
}

OrgChartNode.propTypes = {
    node: PropTypes.object.isRequired,
    onRemoveNode: PropTypes.func.isRequired,
    onRemoveBranch: PropTypes.func.isRequired,
    onAddChild: PropTypes.func.isRequired,
    onDropUser: PropTypes.func.isRequired,
    onSwapUser: PropTypes.func.isRequired,
    onSwapRequest: PropTypes.func.isRequired,
    onSwapNodes: PropTypes.func.isRequired,
    onDragToggle: PropTypes.func.isRequired,
    level: PropTypes.number,
    isDragging: PropTypes.bool,
    zoom: PropTypes.number,
    isMigrationContext: PropTypes.bool,
    migrationInfo: PropTypes.object,
    project: PropTypes.object,
};

/**
 * Main Hierarchy Tree Component
 */
export default function HierarchyTree({
    tree,
    onTreeChange,
    availableUsers,
    isDragging,
    onDragToggle,
    isMigrationContext = false,
    migrationInfo = null,
    project = null
}) {
    const [selectionMode, setSelectionMode] = useState(null); // { nodeId, mode: 'add' | 'swap' }
    const [zoom, setZoom] = useState(1); // 0.5 to 1.5
    const ZOOM_STEP = 0.1;
    const ZOOM_MIN = 0.3;
    const ZOOM_MAX = 1.5;

    // 1. Recursive Remove Branch (Entire subtree)
    const removeBranch = (currentNode, nodeIdToDelete) => {
        if (!currentNode) return null;
        if (currentNode.id === nodeIdToDelete) return null;
        if (currentNode.children) {
            currentNode.children = currentNode.children
                .map(child => removeBranch(child, nodeIdToDelete))
                .filter(child => child !== null);
        }
        return currentNode;
    };

    const handleRemoveBranch = (nodeId) => {
        const updatedTree = removeBranch({ ...tree }, nodeId);
        onTreeChange(updatedTree);
    };

    // 2. Recursive Remove Node ONLY (Promote children)
    const removeNodeOnly = (currentNode, nodeIdToDelete) => {
        if (!currentNode) return null;

        if (currentNode.id === nodeIdToDelete) return null;

        if (currentNode.children) {
            let targetIndex = -1;
            currentNode.children.forEach((child, index) => {
                if (child.id === nodeIdToDelete) targetIndex = index;
            });

            if (targetIndex !== -1) {
                const nodeToDelete = currentNode.children[targetIndex];
                const childrenToPromote = nodeToDelete.children || [];
                const newChildrenList = [...currentNode.children];
                // Replace the node with its children
                newChildrenList.splice(targetIndex, 1, ...childrenToPromote);
                currentNode.children = newChildrenList;
                return currentNode;
            }

            currentNode.children = currentNode.children.map(child => removeNodeOnly(child, nodeIdToDelete));
        }

        return currentNode;
    };

    const handleRemoveNode = (nodeId) => {
        const updatedTree = removeNodeOnly({ ...tree }, nodeId);
        onTreeChange(updatedTree);
    };

    // 3. Recursive Swap User
    const swapUserInNode = (currentNode, nodeId, userId, userName) => {
        if (!currentNode) return null;
        if (currentNode.id === nodeId) {
            currentNode.userId = userId;
            currentNode.name = userName;
            return currentNode;
        }
        if (currentNode.children) {
            currentNode.children = currentNode.children.map(child => swapUserInNode(child, nodeId, userId, userName));
        }
        return currentNode;
    };

    const handleSwapUser = (nodeId, userId, userName) => {
        const updatedTree = swapUserInNode({ ...tree }, nodeId, userId, userName);
        onTreeChange(updatedTree);
    };

    // 4. Recursive Swap Two Nodes
    const findNodeById = (currentNode, id) => {
        if (!currentNode) return null;
        if (currentNode.id === id) return currentNode;
        if (currentNode.children) {
            let found = null;
            currentNode.children.some((child) => {
                found = findNodeById(child, id);
                return found !== null;
            });
            return found;
        }
        return null;
    };

    const handleSwapNodes = (nodeAId, nodeBId) => {
        const nodeA = findNodeById(tree, nodeAId);
        const nodeB = findNodeById(tree, nodeBId);

        if (nodeA && nodeB) {
            const updatedTree = { ...tree };
            
            // Swap user data between the two nodes
            const tempUserId = nodeA.userId;
            const tempName = nodeA.name;

            // We need to perform the swap recursively to ensure we update the tree structure
            const firstPass = swapUserInNode(updatedTree, nodeAId, nodeB.userId, nodeB.name);
            const secondPass = swapUserInNode(firstPass, nodeBId, tempUserId, tempName);

            onTreeChange(secondPass);
        }
    };

    // Recursive add child
    const addChildToNode = (currentNode, parentId, newChild) => {
        if (!currentNode) return currentNode;

        if (currentNode.id === parentId) {
            if (!currentNode.children) {
                currentNode.children = [];
            }
            currentNode.children.push(newChild);
            return currentNode;
        }

        if (currentNode.children) {
            currentNode.children = currentNode.children.map(child => addChildToNode(child, parentId, newChild));
        }

        return currentNode;
    };

    const handleAddChild = (parentId) => {
        setSelectionMode({ nodeId: parentId, mode: 'add' });
    };

    const handleSwapRequest = (nodeId) => {
        setSelectionMode({ nodeId, mode: 'swap' });
    };

    const handleSelectUser = (user) => {
        if (!selectionMode) return;

        let updatedTree;
        if (selectionMode.mode === 'swap') {
            updatedTree = swapUserInNode({ ...tree }, selectionMode.nodeId, user._id, `${user.firstName} ${user.lastName}`);
        } else {
            const newNode = {
                id: `node-${Date.now()}`,
                userId: user._id,
                name: `${user.firstName} ${user.lastName}`,
                role: 'member',
                children: []
            };
            updatedTree = addChildToNode({ ...tree }, selectionMode.nodeId, newNode);
        }

        onTreeChange(updatedTree);
        setSelectionMode(null);
    };

    // Handle drop user onto a node (Add Subordinate)
    const handleDropUser = (targetNodeId, userId, userName, sourceNodeId) => {
        // Prevent moving the Project Leader
        if (sourceNodeId) {
            const sourceNode = findNodeById(tree, sourceNodeId);
            if (sourceNode?.role === 'leader') {
                alert("The Project Leader cannot be moved.");
                return;
            }
        }

        let updatedTree = { ...tree };

        if (sourceNodeId) {
            // "Move" logic: remove from old (promoting children), then add to new
            updatedTree = removeNodeOnly(updatedTree, sourceNodeId);
        }

        const newNode = {
            id: `node-${Date.now()}`,
            userId,
            name: userName,
            role: 'member',
            children: []
        };

        const finalTree = addChildToNode(updatedTree, targetNodeId, newNode);
        onTreeChange(finalTree);
    };

    if (!tree) return null;

    return (
        <Box>
            {/* Zoom Controls */}
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
                spacing={1}
                sx={{ px: 2, py: 0.5, borderBottom: 1, borderColor: 'divider' }}
            >
                <Tooltip title="Zoom Out">
                    <span>
                        <IconButton
                            size="small"
                            onClick={() => setZoom((z) => Math.max(ZOOM_MIN, parseFloat((z - ZOOM_STEP).toFixed(1))))}
                            disabled={zoom <= ZOOM_MIN}
                        >
                            <Iconify icon="eva:minus-circle-outline" width={20} />
                        </IconButton>
                    </span>
                </Tooltip>

                <Slider
                    value={zoom}
                    min={ZOOM_MIN}
                    max={ZOOM_MAX}
                    step={ZOOM_STEP}
                    onChange={(_, val) => setZoom(val)}
                    sx={{ width: 100 }}
                    size="small"
                />

                <Tooltip title="Zoom In">
                    <span>
                        <IconButton
                            size="small"
                            onClick={() => setZoom((z) => Math.min(ZOOM_MAX, parseFloat((z + ZOOM_STEP).toFixed(1))))}
                            disabled={zoom >= ZOOM_MAX}
                        >
                            <Iconify icon="eva:plus-circle-outline" width={20} />
                        </IconButton>
                    </span>
                </Tooltip>

                <Typography variant="caption" sx={{ color: 'text.secondary', minWidth: 40, textAlign: 'center' }}>
                    {Math.round(zoom * 100)}%
                </Typography>

                <Tooltip title="Reset Zoom">
                    <IconButton size="small" onClick={() => setZoom(1)}>
                        <Iconify icon="solar:restart-bold" width={18} />
                    </IconButton>
                </Tooltip>
            </Stack>

            {/* Scrollable Tree */}
            <Box sx={{ overflowX: 'auto', overflowY: 'auto', p: 2 }}>
                <Box sx={{ minWidth: 'fit-content' }}>
                    <OrgChartNode
                        node={tree}
                        onRemoveNode={handleRemoveNode}
                        onRemoveBranch={handleRemoveBranch}
                        onAddChild={handleAddChild}
                        onDropUser={handleDropUser}
                        onSwapUser={handleSwapUser}
                        onSwapRequest={handleSwapRequest}
                        onSwapNodes={handleSwapNodes}
                        onDragToggle={onDragToggle}
                        level={0}
                        isDragging={isDragging}
                        zoom={zoom}
                        isMigrationContext={isMigrationContext}
                        migrationInfo={migrationInfo}
                        project={project}
                    />
                </Box>
            </Box>

            {/* User Selection Panel */}
            {selectionMode && (
                <Paper
                    sx={{
                        position: 'fixed',
                        bottom: 20,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        p: 2,
                        minWidth: 300,
                        maxWidth: 400,
                        boxShadow: 24,
                        zIndex: 1300,
                    }}
                >
                    <Stack spacing={2}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="subtitle2">
                                {selectionMode.mode === 'swap' ? 'Select user to swap with:' : 'Select team member to add:'}
                            </Typography>
                            <IconButton size="small" onClick={() => setSelectionMode(null)}>
                                <Iconify icon="solar:close-circle-bold" />
                            </IconButton>
                        </Stack>

                        {availableUsers.length === 0 ? (
                            <Typography color="text.secondary" variant="body2">No available users</Typography>
                        ) : (
                            <Stack spacing={1} sx={{ maxHeight: 200, overflow: 'auto' }}>
                                {availableUsers.map((user) => (
                                    <Paper
                                        key={user._id}
                                        variant="outlined"
                                        onClick={() => handleSelectUser(user)}
                                        sx={{
                                            p: 1,
                                            cursor: 'pointer',
                                            '&:hover': { bgcolor: 'action.hover', borderColor: 'primary.main' }
                                        }}
                                    >
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                                                {user.firstName?.[0]}{user.lastName?.[0]}
                                            </Avatar>
                                            <Typography variant="body2">
                                                {user.firstName} {user.lastName}
                                            </Typography>
                                        </Stack>
                                    </Paper>
                                ))}
                            </Stack>
                        )}
                    </Stack>
                </Paper>
            )}
        </Box>
    );
}

HierarchyTree.propTypes = {
    tree: PropTypes.object.isRequired,
    onTreeChange: PropTypes.func.isRequired,
    availableUsers: PropTypes.array.isRequired,
    isDragging: PropTypes.bool,
    onDragToggle: PropTypes.func.isRequired,
    isMigrationContext: PropTypes.bool,
    migrationInfo: PropTypes.object,
    project: PropTypes.object,
};
